import { normaliseProfile } from "../profiles/schema.js";
import { convert } from "../convert.js";
import { ruleName, kindLabel } from "../labels.js";
import { state, setDemoProfile, setProfileId } from "../state.js";
import { el, announce, setBusy } from "../ui/dom.js";
import { docxDropzone, readDocx } from "../ui/dropzone.js";
import { CONVERT_ERROR } from "./convert.js";

// Generated into public/demo/ by scripts/build-demo.js during the build.
const DOCUMENT_URL = "demo/bootstrap-showcase.docx";
const DOCUMENT_NAME = "bootstrap-showcase.docx";
const PROFILE_URL = "demo/bootstrap-showcase.mammoth-profile.json";
const EXAMPLES = [
  "Hero Title", "Stat Number", "Stat Label", "Warning Heading", "Warning", "Person Name",
  "List Group Item", "FAQ Question", "FAQ Answer", "Callout", "Badge Pill", "Badge Success",
];

let profileRequest = null; // fetched once per session

function loadProfile() {
  profileRequest ??= fetch(PROFILE_URL).then(async (response) => {
    if (!response.ok) throw new Error(`Profile request failed (${response.status})`);
    const { profile, errors } = normaliseProfile(await response.json());
    if (!profile || errors.length) throw new Error("The demo profile is invalid.");
    return profile;
  });
  profileRequest.catch(() => (profileRequest = null)); // allow a retry
  return profileRequest;
}

export function mount(root, route, nav) {
  const error = el("p", { class: "fire-message", role: "alert", hidden: true });
  const examples = el("tbody", {}, el("tr", {}, el("td", { colspan: "3" }, "Loading the styles…")));
  const { dropzone, input } = docxDropzone({ id: "demo-drop", title: "Drop your edited document here", onFile: (file) => run(() => readDocx(file)) });
  const originalButton = el("button", { type: "button", class: "link-btn" }, "convert the original without editing it");
  originalButton.addEventListener("click", () => run(fetchOriginal));

  const showError = (message) => {
    error.textContent = message;
    error.hidden = !message;
  };

  async function fetchOriginal() {
    const response = await fetch(DOCUMENT_URL);
    if (!response.ok) return { error: "Couldn't load the demo document. Check your connection and try again." };
    return { doc: { name: DOCUMENT_NAME, data: await response.arrayBuffer() } };
  }

  // Converts a document with the demo profile and shows the result.
  async function run(getDoc) {
    showError("");
    setBusy(dropzone, true);
    try {
      const { doc, error: problem } = await getDoc();
      if (problem) return showError(problem);
      let profile;
      try {
        profile = await loadProfile();
      } catch (err) {
        console.error(err);
        return showError("Couldn't load the demo profile. Check your connection and try again.");
      }
      setDemoProfile(profile);
      setProfileId(profile.id);
      try {
        const { html, messages } = await convert(doc.data, state.demoProfile);
        state.doc = doc;
        state.result = { html, messages, profileJson: JSON.stringify(state.demoProfile) };
      } catch (err) {
        console.error(err);
        return showError(CONVERT_ERROR);
      }
      announce(`${doc.name} converted.`);
      nav.go("#/result");
    } finally {
      setBusy(dropzone, false);
    }
  }

  loadProfile().then((profile) => {
    const rows = EXAMPLES.map((name) => profile.rules.find((r) => r.style === name)).filter(Boolean).map((rule) =>
      el("tr", {},
        el("th", { scope: "row" }, ruleName(rule)),
        el("td", {}, kindLabel(rule)),
        el("td", {}, el("code", {}, rule.html))));
    examples.replaceChildren(...rows);
  }).catch(() => {
    examples.replaceChildren(el("tr", {}, el("td", { colspan: "3" }, "Couldn't load the styles.")));
  });

  const step = (number, title, ...content) => el("section", { class: "demo-step glass", "aria-labelledby": `demo-step-${number}` },
    el("h2", { id: `demo-step-${number}` }, el("span", { class: "step-number", "aria-hidden": "true" }, number), title),
    ...content);
  const task = (title, text) => el("li", {}, el("strong", {}, title), " ", text);

  root.append(el("section", { class: "demo-page" },
    el("h1", {}, "Try a demo"),
    el("p", { class: "lead-text" }, "Write in Word, publish clean HTML. Download a sample document, edit it in Word using its styles, then bring it back here to turn it into Bootstrap 5 markup."),

    step(1, "Download the Word document",
      el("p", {}, "It's a quarterly update for a made-up company, with a hero banner, stat cards, alerts, a pricing table, team cards and an FAQ. Every part of it is a named Word style."),
      el("p", {}, el("a", { href: DOCUMENT_URL, download: DOCUMENT_NAME, class: "btn-like primary-btn" }, "Download the Word document (.docx)"))),

    step(2, "Edit it in Word",
      el("p", {}, "Open the document and show the Styles pane (Home tab, then the small arrow beside Styles, or Styles Pane on a Mac). Click into any paragraph to see which style it uses. Then make a few changes, using styles rather than the font and colour buttons:"),
      el("ul", { class: "tasks" },
        task("Add a stat.", "After “Regions, including Perth”, add a paragraph such as 4.9/5 with the Stat Number style, then Customer rating with Stat Label. A fifth stat card appears."),
        task("Add a notice.", "Below “Prices are in Australian dollars…”, type a short heading and a sentence, then apply Warning Heading and Warning. They become one Bootstrap alert."),
        task("Add a team member.", "Copy Priya Raman's four paragraphs, including the photo, paste them below and change the text. The grid gets another card."),
        task("Add a question.", "Add a paragraph with FAQ Question and one with FAQ Answer at the end of the FAQ."),
        task("Add a badge.", "Select a word in a “What shipped” item and apply the Badge Success character style.")),
      el("p", {}, "Save the document as .docx when you're done."),
      el("details", { class: "style-list" },
        el("summary", {}, "Styles you can use and the HTML they become"),
        el("div", { class: "table-scroll" },
          el("table", { class: "mapping-table" },
            el("caption", { class: "sr-only" }, "Word styles in the demo document and the HTML each one produces"),
            el("thead", {}, el("tr", {}, el("th", { scope: "col" }, "Word style"), el("th", { scope: "col" }, "Type"), el("th", { scope: "col" }, "HTML output"))),
            examples)),
        el("p", { class: "hint" }, "The demo profile also includes every style from the built-in Bootstrap 5 profile, such as headings, Quote, Lead and Code Block."))),

    step(3, "Convert it to HTML",
      el("p", {}, "Mammoth Press reads the styles in your edited document and builds the matching Bootstrap 5 markup. You can preview it, copy it or download it as a web page."),
      dropzone, input, error,
      el("p", { class: "hint" }, "Haven't got Word handy? You can ", originalButton, " to see the result.")),

    el("h2", {}, "How it works"),
    el("p", {}, "A profile maps each Word style to HTML. This demo uses the built-in Bootstrap 5 profile plus extra rules for cards, stats and FAQs. Paragraphs in a row that share a style join into the same component, which is why three Person paragraphs become a three-column grid. After converting, you can open the profile in the editor to see or change every rule, or ",
      el("a", { href: PROFILE_URL, download: "bootstrap-showcase.mammoth-profile.json" }, "download the profile"),
      " and import it on the ", el("a", { href: "#/profiles" }, "Profiles"), " page.")));

  return { title: "Try a demo" };
}
